import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { DateFormatService } from '@lx/shared/i18n';
import { FormsModule } from '@angular/forms';
import { FormFieldComponent } from '@lx/shared/ui/form-field';
import { OverlayOptions } from 'primeng/api';
import { BreakpointObserverService } from '@lx/shared/utils-dom';
import { Subject, takeUntil } from 'rxjs';
import { FocusTrapModule } from 'primeng/focustrap';
import { InputTextModule } from 'primeng/inputtext';
import { InputMaskModule } from 'primeng/inputmask';
import { ClickOutsideDirective } from '@lx/shared/ui/directives';

const BASE_Z_INDEX = -1000;

@Component({
  standalone: true,
  selector: 'lx-date-of-birth-field',
  imports: [
    CommonModule,
    InputNumberModule,
    DropdownModule,
    FormsModule,
    FormFieldComponent,
    FocusTrapModule,
    InputTextModule,
    InputMaskModule,
    ClickOutsideDirective,
  ],
  templateUrl: './date-of-birth-field.component.html',
  styleUrls: ['./date-of-birth-field.component.scss'],
})
export class DateOfBirthFieldComponent implements OnInit, OnDestroy {
  @Input() date: Date | undefined;
  @Input() label: string | undefined;
  @Input() isValid: null | boolean = false;
  @Input() required = false;
  @Input() errorMessage: string | undefined;

  @Output() dateChange = new EventEmitter<Date>();
  @Output() componentFocusOut = new EventEmitter<FocusEvent>();

  months: { name: string; code: number }[];
  day: number | undefined;
  month: number | undefined;
  year: number | undefined;
  overlayOption: OverlayOptions = {
    autoZIndex: true,
    baseZIndex: BASE_Z_INDEX,
  };
  monthScrollHeight = '500px';

  private interactionDetected = false;
  private touched = false;

  private readonly destroy$ = new Subject<void>();

  private readonly breakpointObserverService = inject(BreakpointObserverService);

  ngOnInit(): void {
    this.prepareMonths();

    this.breakpointObserverService.isMobile$.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      if (isMobile) {
        this.monthScrollHeight = '200px';
      } else {
        this.monthScrollHeight = '500px';
      }
    });

    if (this.date) {
      this.day = this.date.getDate();
      this.month = this.date.getMonth();
      this.year = this.date.getFullYear();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateDateOfBirth() {
    if (this.isValidDate() && this.isValidDay()) {
      const date = new Date(this.year, this.month, this.day);
      this.dateChange.emit(date);
    } else {
      this.dateChange.emit(null);
    }
  }

  markTouchedOnOutsideFocus() {
    if (!this.interactionDetected || this.touched) {
      return;
    }
    this.componentFocusOut.emit();
    this.touched = true;
  }

  onInternalInteraction() {
    this.interactionDetected = true;
  }

  private prepareMonths() {
    const calendarConfig = DateFormatService.getCalendarLocaleSettings().monthNames;
    this.months = calendarConfig.map((name, index) => ({
      name,
      code: index,
    }));
  }

  private isValidDate() {
    return this.day && this.month !== null && this.month !== undefined && this.year && this.isValidYear();
  }

  private isValidYear() {
    return this.year?.toString()?.length === 4 && this.year >= 1900 && this.year <= new Date().getFullYear();
  }

  private isValidDay() {
    // Check that the number of days corresponds to the month and year and is never greater than 31.
    return this.day < 32 && !(this.year && this.month && this.day > new Date(this.year, this.month + 1, 0)?.getDate());
  }
}
